import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { ProductState, Product, Category, ProductSearchParams, PaginatedResponse } from '../../types';

export const fetchProducts = createAsyncThunk<PaginatedResponse<Product> | Product[], ProductSearchParams>(
  'products/fetchProducts',
  async ({ page = 0, size = 12, searchTerm = '', category = '', sortBy = 'title', sortDirection = 'asc' }) => {
    // Parse sortBy to separate sort field and direction
    const [sortField, direction] = sortBy.includes(',') ? sortBy.split(',') : [sortBy, sortDirection];
    
    // Convert camelCase to snake_case for API
    const sortFieldMap: { [key: string]: string } = {
      'createdAt': 'created_at',
      'averageRating': 'average_rating',
      'title': 'title',
      'price': 'price',
      'featured': 'featured'
    };
    
    const mappedSortField = sortFieldMap[sortField] || sortField;
    
    const params = new URLSearchParams({
      page: (page + 1).toString(), // PHP backend uses 1-based pagination
      per_page: size.toString(),
      ...(searchTerm && { search: searchTerm }),
      ...(category && { category_id: category }),
      sort: mappedSortField,
      direction: direction,
    });
    const response = await api.get(`/products?${params}`);
    
    // Handle PHP Laravel response format
    if (response.data.products && response.data.pagination) {
      return {
        content: response.data.products,
        totalPages: response.data.pagination.last_page,
        totalElements: response.data.pagination.total,
        number: response.data.pagination.current_page - 1, // Convert to 0-based
        size: response.data.pagination.per_page,
      };
    }
    
    return response.data;
  }
);

export const fetchProductById = createAsyncThunk<Product, number>(
  'products/fetchProductById',
  async (productId) => {
    const response = await api.get(`/products/${productId}`);
    return response.data.product;
  }
);

export const fetchCategories = createAsyncThunk<Category[], void>(
  'products/fetchCategories',
  async () => {
    const response = await api.get('/categories');
    return response.data.categories || response.data || [];
  }
);

export const fetchAdminCategories = createAsyncThunk<Category[], void>(
  'products/fetchAdminCategories',
  async () => {
    const response = await api.get('/categories');
    return response.data.categories || response.data || [];
  }
);

const initialState: ProductState = {
  products: [],
  currentProduct: null,
  categories: [],
  loading: false,
  error: null,
  totalPages: 0,
  totalElements: 0,
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
          state.totalPages = 1;
          state.totalElements = action.payload.length;
        } else {
          state.products = action.payload.content;
          state.totalPages = action.payload.totalPages;
          state.totalElements = action.payload.totalElements;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      });

    // Fetch Product By Id
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch product';
      });

    // Fetch Categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      });

    // Fetch Admin Categories
    builder
      .addCase(fetchAdminCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchAdminCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch admin categories';
      });
  },
});

export const { clearCurrentProduct } = productSlice.actions;
export default productSlice.reducer;